import { useCallback, useEffect, useState } from 'react';
import { resolveWidgetMediaUrl } from '@/lib/resolveWidgetMediaUrl';
import type { ChatAttachmentView } from '@/components/ChatMessageAttachments';

type Props = {
  images: ChatAttachmentView[];
};

/**
 * WhatsApp-style image album for PWA chat: up to 4 tiles, +N on the 4th, lightbox.
 */
export function ChatImageAlbum({ images }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const resolved = images
    .map((a) => {
      const raw = a.url || '';
      return { ...a, src: raw ? resolveWidgetMediaUrl(raw) : '' };
    })
    .filter((a) => Boolean(a.src));

  const close = useCallback(() => setOpenIndex(null), []);
  const showPrev = useCallback(() => {
    setOpenIndex((i) => (i == null ? i : (i - 1 + resolved.length) % resolved.length));
  }, [resolved.length]);
  const showNext = useCallback(() => {
    setOpenIndex((i) => (i == null ? i : (i + 1) % resolved.length));
  }, [resolved.length]);

  useEffect(() => {
    if (openIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, close, showPrev, showNext]);

  if (resolved.length === 0) return null;

  const total = resolved.length;
  const overflow = total > 4 ? total - 3 : 0;
  const tiles =
    total === 1 ? resolved.slice(0, 1)
    : total === 2 ? resolved.slice(0, 2)
    : total === 3 ? resolved.slice(0, 3)
    : resolved.slice(0, 4);

  return (
    <>
      <div
        className={`chat-image-album${total === 1 ? ' chat-image-album--single' : ''}${total === 3 ? ' chat-image-album--three' : ''}`}
        role="group"
        aria-label={`${total} fotos`}
      >
        {tiles.map((tile, idx) => {
          const isOverflowTile = overflow > 0 && idx === 3;
          const openAt = isOverflowTile ? 3 : idx;
          return (
            <button
              key={`${tile.src}-${idx}`}
              type="button"
              className={`chat-image-album__tile${isOverflowTile ? ' chat-image-album__tile--more' : ''}${total === 3 && idx === 2 ? ' chat-image-album__tile--span' : ''}`}
              onClick={() => setOpenIndex(openAt)}
              aria-label={
                isOverflowTile ? `Ver ${overflow} fotos más` : tile.caption || `Foto ${idx + 1}`
              }
            >
              <img src={tile.src} alt={tile.caption || 'Foto'} loading="lazy" />
              {isOverflowTile ?
                <span className="chat-image-album__more">+{overflow}</span>
              : null}
            </button>
          );
        })}
      </div>

      {openIndex != null && resolved[openIndex] ?
        <div
          className="chat-image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Galería de fotos"
          onClick={close}
        >
          <button type="button" className="chat-image-lightbox__close" onClick={close} aria-label="Cerrar">
            ×
          </button>
          {resolved.length > 1 ?
            <>
              <button
                type="button"
                className="chat-image-lightbox__nav chat-image-lightbox__nav--prev"
                aria-label="Anterior"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className="chat-image-lightbox__nav chat-image-lightbox__nav--next"
                aria-label="Siguiente"
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
              >
                ›
              </button>
            </>
          : null}
          <img
            className="chat-image-lightbox__img"
            src={resolved[openIndex]!.src}
            alt={resolved[openIndex]!.caption || 'Foto'}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="chat-image-lightbox__counter">
            {openIndex + 1} / {resolved.length}
          </span>
        </div>
      : null}
    </>
  );
}
